/* パスワード変更（職員、お客様共通） */
import React, { useState, useEffect } from 'react';
import Block from '../molecules/Block';
import Text from '../molecules/Text';
import Annotation from '../atoms/Annotation';
import CenterButton from '../atoms/CenterButton';
import axios from '../axios';
import { Row, Col, Modal, Button } from 'react-bootstrap';
import { handleBrowzerBack } from '../BrowzerBack';
import 'url-search-params-polyfill';
import LoadingModal from '../molecules/LoadingModal';
import { isBrowserIe } from '../BrowserJudgment';
import { jumpUrl } from '../Common';
import { createRoot } from 'react-dom/client';

const PasswordChange: React.FC = () => {
  // 処理結果通知ダイアログ
  const [show, setShow] = useState(false);
  const handleShow = () => setShow(true);
  const handleClose = () => setShow(false);

  const successDialogMessage = 'パスワードを変更しました。';
  const failDialogMessage = 'パスワードの変更が失敗しました。';

  // パスワード有効期限のフラグ
  const [password_expired, setpPasswordExpire] = useState();
  // パスワード
  const [password, setPassword] = useState('');
  // パスワード(確認)
  const [password_confirmation, setPasswordConfirmation] = useState('');
  const [password_change_message, setPasswordChangeResult] = useState('');
  const [dialog_message, setDialogMessage] = useState('');
  // 申込人か否か
  const [is_applicant, setIsApplicant] = useState(false);
  const [status_code, setStatusCode] = useState(0);
  // API通信中フラグ
  const [is_connecting, setIsConnecting] = useState(false);

  //ローディング画面表示状態
  const [is_loading, setIsLoading] = useState(false);
  useEffect(() => {
    // ブラウザの戻る・進む禁止
    handleBrowzerBack();
  }, []);
  // モーダルクローズ & ログイン画面に遷移
  const redirectToLogin = () => {
    const location_url = location.search;
    const email_hash = new URLSearchParams(location_url).get('email_hash');

    //リセットメールからの遷移時の場合はログイン画面へ遷移する。
    if (email_hash !== undefined && email_hash !== null) {
      location.replace(`${process.env.MIX_MOCK_URL}/mypage/login`);
    } else {
      // ログイン画面へ遷移する
      const url = is_applicant
        ? `${process.env.MIX_MOCK_URL}/mypage/login`
        : `${process.env.MIX_MOCK_URL}/login`;
      location.replace(url);
    }
  };

  // パスワード変更ボタンのクリックイベントを定義
  const handlePasswordChange = () => {
    //メールからの画面遷移の場合
    const location_url = location.search;
    const email_hash = new URLSearchParams(location_url).get('email_hash');

    // ログイン情報をStateへセット
    // 結果表示ボックスに適用するCSSクラス名
    const password_info = {
      password: password,
      password_confirmation: password_confirmation,
      email_hash: email_hash !== null ? email_hash : '',
    };

    const passwordChange = async () => {
      try {
        if (is_connecting) {
          return;
        }
        //ローディング画面表示
        setIsLoading(true);
        setIsConnecting(true);

        const url = `/password/change`;
        const response = await axios.post(url, password_info);
        setIsApplicant(response.data.is_applicant);
        setDialogMessage(successDialogMessage);
        setStatusCode(response.status);

        // パスワードの有効期限切れ判定
        if (
          typeof response.data.password_expired !== 'undefined' &&
          response.data.password_expired
        ) {
          // パスワード有効期限切れフラグをStateへセット
          setpPasswordExpire(response.data.password_expired);
        }

        // ダイアログ表示
        handleShow();
        setIsConnecting(false);
      } catch (error) {
        // 失敗
        const is_login = document.getElementById('is_login') as HTMLInputElement;

        if (is_applicant) {
          //行員の場合
          if (is_login !== null && !is_login.value) {
            // 未ログインの場合
            jumpLoginDisp();
          } else {
            // ログイン済の場合
            jumpUrl(error.response.data.url);
          }
        } else {
          //申込人の場合
          jumpUrl(error.response.data.url);
        }
        setPasswordChangeResult(error.response.data.message);
        setStatusCode(error.response.status);
        setDialogMessage(failDialogMessage);
        setIsConnecting(false);
      } finally {
        //ローディング画面非表示
        setIsLoading(false);
      }
    };
    // パスワード変更処理
    passwordChange();
  };

  // 要素のセンタリングのクラス名を動的に生成する（IEは生成しない、それ以外は生成する）
  const justify_content_center = isBrowserIe() ? '' : 'justify-content-center';

  /**
   * 戻るボタンをクリックする
   */
  const handleClickReturn = () => {
    jumpLoginDisp();
  };

  /**
   * ログイン/リスト画面へジャンプ
   */
  const jumpLoginDisp = () => {
    // domからログインユーザー情報を読み取る
    const is_applicant = document.getElementById('is_applicant') as HTMLInputElement;
    const is_login = document.getElementById('is_login') as HTMLInputElement;

    if (is_applicant !== null && !is_applicant.value) {
      // 行員
      location.replace(
        !is_login.value
          ? `${location.origin}${process.env.MIX_MOCK_URL}`
          : `${location.origin}${process.env.MIX_MOCK_URL}/staff/list` // ログインしている場合
      );
    } else {
      // 申込人
      location.replace(
        is_login.value
          ? `${location.origin}${process.env.MIX_MOCK_URL}/mypage/login`
          : `${location.origin}${process.env.MIX_MOCK_URL}/mypage/list` // ログインしている場合
      );
    }
  };

  return (
    <>
      <div className="mt-5"></div>
      <Block
        label="パスワード変更"
        content={
          <>
            <Annotation text="新しいパスワードを入力してください。" />
            <div className="mt-3"></div>
            <div>
              <Text
                id="password"
                label="パスワード"
                type="password"
                value={password}
                placeholder=""
                handleChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setPassword(event.target.value)
                }
                disabled={false}
                maxlength={200}
              />
            </div>
            <div className="mt-3">
              <Text
                id="password-confirm"
                label="パスワード（確認）"
                type="password"
                value={password_confirmation}
                placeholder=""
                handleChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setPasswordConfirmation(event.target.value)
                }
                disabled={false}
                maxlength={200}
              />
            </div>
            <div className="mt-3"></div>
            <Annotation text="※パスワードは、英字・数字を含む8文字以上で設定してください。" />
            <div className="mt-3"></div>
            <Row className="d-flex align-items-center justify-content-center">
              <Col></Col>
              {password_change_message !== undefined &&
                // パスワード変更失敗メッセージあり
                status_code !== 201 && (
                  // 失敗
                  <Col md={9} className="alert-danger alert-danger-custom">
                    {password_change_message}
                  </Col>
                )}
            </Row>
          </>
        }
      />
      <div className="mt-5" />
      <CenterButton label="パスワード変更" handleClick={handlePasswordChange} />
      <div className="mt-4 text-center">
        <Button
          variant="main-color"
          className="return-button"
          size="lg"
          onClick={handleClickReturn}
        >
          戻る
        </Button>
      </div>
      <div className="mt-4" />
      {/* モーダル */}
      <Modal
        className={`d-flex align-items-center ${justify_content_center}`}
        show={show}
        onHide={
          password_change_message !== undefined && status_code == 201
            ? redirectToLogin
            : handleClose
        }
        animation={false}
      >
        <Modal.Header>
          <Modal.Title className="dialog-title"></Modal.Title>
        </Modal.Header>
        <Modal.Body className="dialog-body">{dialog_message}</Modal.Body>
        <Modal.Footer>
          {password_change_message !== undefined &&
            // パスワード変更結果メッセージあり
            (status_code == 201 ? (
              // 成功
              <Button variant="primary" onClick={redirectToLogin}>
                OK
              </Button>
            ) : (
              // 失敗
              <Button variant="secondary" onClick={handleClose}>
                OK
              </Button>
            ))}
        </Modal.Footer>
      </Modal>
      {/* ローディング画面 */}
      <LoadingModal show={is_loading} />
    </>
  );
};
const container = document.getElementById('common-password-change');
if (container) {
  const root = createRoot(container);
  root.render(<PasswordChange />);
}
